import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqApiServiceListSource,
    LsqListSource,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import {
    ListResponseSupplierContactDto,
    ListSupplierContactsRequest,
    ListSupplierContactsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/supplier-details/list-supplier-contacts';
import { SupplierContactDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/supplier-contact-dto';
import {
    Color,
    forLifeOf,
    getRouterLinkUrlById,
    LifecycleAware,
    TableAppearance
} from '@lsq-frontend/common-ui';
import { Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { of } from 'rxjs';
import {
    atlas,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';

@LifecycleAware()
@Component({
    selector: 'supplier-contacts-table',
    template: `
        <card
            *ngIf="!(responsiveService.mdBelow | async)"
            [headerTitle]="supplierContactsCardHeaderI18n"
            id="business-units-table-card"
        >
            <div card-body *ngIf="!!dataSource">
                <lsq-data-table
                    [dataSource]="(dataSource.noData$ | async) ? nullDataSource : dataSource"
                    [layoutFixed]="true"
                    [isPaginated]="(dataSource.noData$ | async) ? false : true"
                    [takeOptions]="[5]"
                    [disableTakeSelect]="true"
                >
                    <lsq-column
                        [nullDataDash]="true"
                        [column]="{
                            header: contactNameHeaderI18n,
                            key: 'name',
                            truncateTextCss: true,
                            width: '25%',
                            sortable: true
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [nullDataDash]="true"
                        [column]="{
                            header: contactTitleHeaderI18n,
                            key: 'title',
                            width: '25%',
                            truncateTextCss: true,
                            sortable: true
                        }"
                    >
                    </lsq-column>
                    <lsq-phone-column
                        [nullDataDash]="true"
                        [column]="{
                            header: contactPhoneHeaderI18n,
                            key: 'phone',
                            width: '25%',
                            sortable: true
                        }"
                    >
                    </lsq-phone-column>
                    <lsq-column
                        [nullDataDash]="true"
                        [column]="{
                            header: contactEmailHeaderI18n,
                            key: 'email',
                            width: '25%',
                            truncateTextCss: true,
                            sortable: true
                        }"
                    >
                    </lsq-column>
                </lsq-data-table>
            </div>
        </card>

        <div *ngIf="responsiveService.mdBelow | async">
            <h1>{{ supplierContactsCardHeaderI18n }}</h1>
            <lsq-data-table-mobile
                [dataSource]="dataSource"
                [isPaginated]="true"
                [takeOptions]="[5]"
            >
                <ng-template #mobileCardHeader let-row="row">
                    <div class="mtc-header-content">
                        <div class="mtc-header-title">
                            {{ row.name }}
                        </div>
                        <div class="mtc-header-sub-title">{{ row.title || '—' }}</div>
                    </div>
                </ng-template>
                <ng-template #mobileCardColumns let-row="row">
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ contactPhoneHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.phone ? (row.phone | phone) : '—' }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ contactEmailHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.email || '—' }}
                        </div>
                    </div>
                </ng-template>
            </lsq-data-table-mobile>
        </div>
    `
})
export class SupplierContactsTableComponent implements OnInit {
    public dataSource!: LsqApiServiceListSource<
        ListResponseSupplierContactDto,
        ListSupplierContactsRequest
    >;

    private _nullContactRow: SupplierContactDto = {
        name: undefined,
        phone: undefined,
        title: undefined,
        email: undefined
    };

    public nullDataSource = new LsqListSource<SupplierContactDto>({
        data$: of({
            rows: [this._nullContactRow],
            pagination: {
                take: 5,
                skip: 0
            },
            totalCount: 0
        }),
        rows$: of([this._nullContactRow])
    });

    public readonly supplierContactsSearchFormGroup = new FormGroup({
        supplierContactsName: new FormControl()
    });

    public readonly icons = Icons;
    public readonly color = Color;
    public readonly tableAppearance = TableAppearance;

    public readonly supplierContactsCardHeaderI18n = this._i18n({
        value: 'Supplier Contacts',
        meaning: `Description of the card contents`,
        description: 'Card header'
    });
    public readonly contactNameHeaderI18n = this._i18n({
        value: 'Name',
        meaning: `Name of the supplier contact`,
        description: 'Table column header'
    });
    public readonly contactTitleHeaderI18n = this._i18n({
        value: 'Title',
        meaning: `Title of the supplier contact`,
        description: 'Table column header'
    });
    public readonly contactPhoneHeaderI18n = this._i18n({
        value: 'Phone',
        meaning: `Phone number of the supplier contact`,
        description: 'Table column header'
    });
    public readonly contactEmailHeaderI18n = this._i18n({
        value: 'Email',
        meaning: `Email of the supplier contact`,
        description: 'Table column header'
    });

    constructor(
        public responsiveService: ResponsiveService,
        private _i18n: I18n,
        private _listSupplierContactsService: ListSupplierContactsService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute
    ) {}

    public ngOnInit(): void {
        this._activatedRoute.params.pipe(forLifeOf(this)).subscribe(async (params) => {
            try {
                this.dataSource = new LsqApiServiceListSource<
                    ListResponseSupplierContactDto,
                    ListSupplierContactsRequest
                >(
                    this._listSupplierContactsService,
                    new ListSupplierContactsRequest({
                        filter: {
                            supplierKey: params?.sellerKey
                        },
                        pagination: {
                            sortColumn: 'name',
                            sortDirection: SortDirectionFinancing.ASCENDING,
                            skip: 0,
                            take: 5
                        }
                    })
                );
            } catch (e) {
                this._router.navigateByUrl(
                    getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS)
                );
            }
        });
    }
}
